import React, { useState, useEffect, useRef } from 'react'
import { IoMdSearch, IoIosArrowUp, IoIosArrowDown, IoMdClose } from 'react-icons/io'
import styles from './styles.module.scss'

/**
 * Переиспользуемый компонент поиска и сортировки по массиву по одному ключу.
 *
 * props:
 * - data: исходный массив объектов
 * - searchKey: ключ объекта, по которому выполняется поиск/сортировка
 * - onFilter: (filteredData | null, query: string) => void
 *   - передаёт отфильтрованный массив или null (если строка пустая)
 * - onSort: (sortedData, sortKey, sortOrder) => void
 *   - передаёт отсортированный массив, ключ сортировки и порядок ('asc' | 'desc' | null)
 * - activeSortKey: текущий активный ключ сортировки (для синхронизации между колонками)
 * - activeSortOrder: текущий активный порядок сортировки
 * - onSearchOpen: (searchKey: string | null) => void - колбэк для уведомления об открытии/закрытии поиска
 * - openSearchKey: string | null - ключ открытого поиска (для синхронизации между компонентами)
 */
const TableSearch = ({ data = [], searchKey, onFilter, onSort, activeSortKey, activeSortOrder, withoutSearch, onSearchOpen, openSearchKey }) => {
    const [open, setOpen] = useState(false)
    const [query, setQuery] = useState('')
    const wrapperRef = useRef(null)
    const onFilterRef = useRef(onFilter)
    const onSortRef = useRef(onSort)
    const onSearchOpenRef = useRef(onSearchOpen)

    // Определяем локальное состояние сортировки на основе активной сортировки
    const sortOrder = activeSortKey === searchKey ? activeSortOrder : null

    // Обновляем ref при изменении callbacks, но не триггерим эффект
    useEffect(() => {
        onFilterRef.current = onFilter
        onSortRef.current = onSort
        onSearchOpenRef.current = onSearchOpen
    }, [onFilter, onSort, onSearchOpen])

    // Закрываем поиск, если открыт другой поиск (но не этот)
    useEffect(() => {
        if (openSearchKey !== null && openSearchKey !== searchKey && open) {
            // Если открыт другой поиск и этот тоже открыт - закрываем этот
            setOpen(false)
            setQuery('')
            if (onFilterRef.current) {
                onFilterRef.current(null, '')
            }
        }
    }, [openSearchKey, searchKey, open])


    useEffect(() => {
        if (!onFilterRef.current || !searchKey) return

        const trimmed = query.trim()

        if (!trimmed) {
            onFilterRef.current(null, '')
            return
        }

        const lower = trimmed.toLowerCase()

        const filtered = (data || []).filter((item) => {
            const value = item?.[searchKey]
            if (value === null || value === undefined) return false
            return String(value).toLowerCase().includes(lower)
        })

        onFilterRef.current(filtered, trimmed)
    }, [query, data, searchKey])

    const handleIconClick = () => {
        const newOpenState = !open
        setOpen(newOpenState)
        if (onSearchOpenRef.current) {
            onSearchOpenRef.current(newOpenState ? searchKey : null)
        }
    }

    const handleClose = () => {
        setOpen(false)
        setQuery('')
        if (onFilterRef.current) {
            onFilterRef.current(null, '')
        }
        if (onSearchOpenRef.current) {
            onSearchOpenRef.current(null)
        }
    }

    const handleChange = (event) => {
        setQuery(event.target.value)
    }

    const handleSortClick = (e) => {
        e.stopPropagation()
        if (!onSortRef.current || !searchKey) return

        let newOrder = null
        if (sortOrder === null) {
            newOrder = 'asc'
        } else if (sortOrder === 'asc') {
            newOrder = 'desc'
        } else {
            newOrder = null
        }

        // Просто передаем информацию о сортировке родительскому компоненту
        // Родительский компонент сам будет применять сортировку через useMemo
        onSortRef.current(null, searchKey, newOrder)
    }

    return (
        <div className={styles.searchWrapper} ref={wrapperRef}>
            {!withoutSearch &&
                <button
                    type="button"
                    className={styles.iconButton}
                    onClick={handleIconClick}
                    title="Поиск по колонке"
                >
                    <IoMdSearch size={18} />
                </button>
            }

            {onSort && (
                <button
                    type="button"
                    className={styles.sortButton}
                    onClick={handleSortClick}
                    title="Сортировка"
                >
                    {sortOrder === 'asc' ? (
                        <IoIosArrowUp size={16} className={styles.sortIconActive} />
                    ) : sortOrder === 'desc' ? (
                        <IoIosArrowDown size={16} className={styles.sortIconActive} />
                    ) : (
                        <div className={styles.sortIcons}>
                            <IoIosArrowUp size={12} />
                            <IoIosArrowDown size={12} />
                        </div>
                    )}
                </button>
            )}

            {open && (
                <div className={styles.popover}>
                    <input
                        autoFocus
                        type="text"
                        value={query}
                        onChange={handleChange}
                        className={styles.input}
                        placeholder="Поиск..."
                    />
                    <button
                        type="button"
                        className={styles.closeButton}
                        onClick={handleClose}
                        title="Закрыть"
                    >
                        <IoMdClose size={16} />
                    </button>
                </div>
            )}
        </div>
    )
}

export default TableSearch


